import InfoIcon from '@mui/icons-material/Info';
import {
  Divider,
  Grid,
  Stack,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { isEmpty } from 'lodash-es';
import { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { getRazorPayProratedCharge, manageRazorPayCharge } from 'wisestep-api';
import {
  Button,
  ControlledTextInput,
  Item,
  WsButton,
  WsColors,
  WsTypoColors,
  WsTypography,
  WsTypoVariants,
} from 'wisestep-base';

import { useWorkspaceSKUContext } from '../../../context';
import { failed } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import { IPaymentInfo } from '../types/IPaymentInfo';
import {
  getIntervalFromSKU,
  getNoOfMonthFromInterval,
  getPlanCode,
  getRazorPayProductId,
  getRazorPlanRateByInterval,
} from '../utils/helper';

const StyledTableCell = styled(TableCell)(() => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: WsColors.TertiaryShades100,
    color: WsColors.GenericRichBlack,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const AlingTextSideBySide = ({
  leftText,
  rightText,
  leftTextCaption,
  primary,
}: {
  leftText: string;
  rightText: string;
  leftTextCaption?: string;
  primary?: boolean;
}) => (
  <Grid container mt={2}>
    <Grid item xs={6} textAlign="left">
      <WsTypography bold> {leftText}</WsTypography>
      {leftTextCaption && (
        <WsTypography bold variant={WsTypoVariants.BODY2} color={WsTypoColors.QUINARY}>
          {leftTextCaption}
        </WsTypography>
      )}
    </Grid>
    <Grid item xs={6} textAlign="right">
      <WsTypography bold color={primary ? WsTypoColors.PRIMARY : WsTypoColors.BLACK}>
        {rightText}
      </WsTypography>
    </Grid>
    <Divider sx={{ width: '100%', mt: 1 }} />
  </Grid>
);

const RazorPayManagePlan = ({
  plan,
  handleClose,
}: {
  plan: IPaymentInfo;
  handleClose: () => void;
}) => {
  const [warnBeforePayment, setWarnBeforePayment] = useState(false);
  const [disableSubmit, setDisableSubmit] = useState(false);
  const workspaceSKU = useWorkspaceSKUContext();
  const [tempUsers, setTempUsers] = useState<number>(0);
  const [totalCharge, setTotalCharge] = useState<string>('0');
  const navigate = useNavigate();
  const [proratedAmount, setProratedAmount] = useState<string>('0');
  const [subTotal, setSubTotal] = useState(0);
  const razorPayProductId = getRazorPayProductId(plan.plan, plan.time);
  const setNotification = useNotificationStore((state) => state.setNotification);
  const methods = useForm({
    defaultValues: {
      users: 0,
    },
  });
  const { setValue, watch, getValues } = methods;
  const totalUsers = watch('users');

  const currentPlanInterval = getIntervalFromSKU(workspaceSKU.activeFrom, workspaceSKU.expiryDate);

  const noOfMonth = getNoOfMonthFromInterval(currentPlanInterval);

  const currentRate = parseFloat(
    (getRazorPlanRateByInterval(plan.plan, currentPlanInterval) / noOfMonth).toFixed(2)
  );

  useEffect(() => {
    let perUserCharge = parseFloat((totalUsers * currentRate).toFixed(2));
    switch (currentPlanInterval) {
      case 'month':
        perUserCharge = perUserCharge * 1;
        break;
      case 'quater':
        perUserCharge = perUserCharge * 3;
        break;
      case 'annual':
        perUserCharge = perUserCharge * 12;
        break;
    }
    setTotalCharge(perUserCharge.toFixed(2));
  }, [currentPlanInterval, currentRate, noOfMonth, totalUsers]);

  const onInputChange = async (e) => {
    const { value } = e.target;
    if (totalUsers != tempUsers && currentPlanInterval != 'none') {
      setProratedAmount('calculating');
      setTempUsers(totalUsers);
      if (value <= 0) {
        setNotification(failed(`Users cannot be less then 0`));
        setValue('users', 0);
        setTotalCharge('0');
      } else {
        const res = await getRazorPayProratedCharge({
          users: value,
          amount: parseInt(totalCharge),
          productId: razorPayProductId,
          interval: currentPlanInterval,
          planStartDate: workspaceSKU?.activeFrom
            ? parseInt((new Date(workspaceSKU.activeFrom).getTime() / 1000).toFixed(0)).toString()
            : '',
        });
        const total = parseFloat(res.data);
        setProratedAmount(total.toFixed(2));
        setSubTotal(parseFloat((total - (total * 18) / 100).toFixed(2)));
      }
    }
  };

  const onSubmit = async () => {
    try {
      setDisableSubmit(true);
      const planInfo = getPlanCode(plan?.plan);
      if (!isEmpty(planInfo)) {
        await manageRazorPayCharge({
          amount: parseInt(proratedAmount),
          productId: razorPayProductId,
          interval: plan.time,
          customerId: plan.customerId,
          users: getValues('users')?.toString() ?? '0',
          manual: plan.manual,
          planCode: planInfo,
          userManagement: false,
        });
        navigate('/plan/purchase??success=true&update=true');
      } else {
        navigate('/plan/purchase??success=false');
        setNotification(failed('Unable to detect plan'));
      }
    } catch (e) {
      setDisableSubmit(false);
    }
  };

  const setPlanInterval = () => {
    let interval = '';
    switch (currentPlanInterval) {
      case 'month':
        interval = '1 Month';
        break;
      case 'quater':
        interval = '1 Quater';
        break;
      case 'annual':
        interval = '1 Year';
        break;
    }
    return interval;
  };

  const setPlanIntervalInMonths = () => {
    let interval = '';
    switch (currentPlanInterval) {
      case 'month':
        interval = '1 Month';
        break;
      case 'quater':
        interval = '3 Months';
        break;
      case 'annual':
        interval = '12 Months';
        break;
    }
    return interval;
  };

  return (
    <>
      <Grid container>
        {!warnBeforePayment ? (
          <FormProvider {...methods}>
            <Grid item xs={12} textAlign="center">
              <WsTypography variant={WsTypoVariants.TITLE} color={WsTypoColors.PRIMARY} bold>
                Your Cart
              </WsTypography>
            </Grid>
            <Grid item xs={12} mt={3}>
              <TableContainer>
                <Table sx={{ minWidth: 700 }} aria-label="customized table">
                  <TableHead>
                    <TableRow>
                      <StyledTableCell>
                        <WsTypography bold>S.no</WsTypography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <WsTypography bold>Plan</WsTypography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <WsTypography bold>Price Per User</WsTypography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <WsTypography bold>Number of Users</WsTypography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <WsTypography bold>Plan interval</WsTypography>
                      </StyledTableCell>
                      <StyledTableCell>
                        <WsTypography bold>Total</WsTypography>
                      </StyledTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell component="th" scope="row">
                        <WsTypography bold color={WsTypoColors.QUINARY}>
                          1.
                        </WsTypography>
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <WsTypography bold color={WsTypoColors.QUINARY}>
                          {plan.plan}
                        </WsTypography>
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <WsTypography bold color={WsTypoColors.QUINARY}>
                          {`₹${currentRate}`}
                        </WsTypography>
                      </TableCell>
                      <TableCell component="th" scope="row">
                        <ControlledTextInput
                          name="users"
                          labelText=""
                          type="number"
                          fullWidth
                          id="users"
                          placeholder=""
                          InputProps={{ inputProps: { min: 1 } }}
                          variant="outlined"
                          autoComplete="users"
                          sx={{ width: 150 }}
                          onMouseLeave={onInputChange}
                        />
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {setPlanInterval()}
                      </TableCell>
                      <TableCell component="th" scope="row">
                        {`₹ ${currentRate} x ${setPlanIntervalInMonths()} including GST`}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Grid>
            <Grid item xs={8}>
              <Item sx={{ mt: 4, pb: 0 }}>
                <a href="https://razorpay.com/" target="_blank" rel="noreferrer">
                  <img
                    src="https://badges.razorpay.com/badge-light.png"
                    style={{ height: '45px', width: '113px' }}
                    alt="Razorpay | Payment Gateway | Neobank"
                  />
                </a>
              </Item>
            </Grid>
            <Grid item xs={4} mt={1}>
              <AlingTextSideBySide leftText="Subtotal:" rightText={`₹${subTotal}`} />
              {plan.sameStateGST ? (
                <>
                  <AlingTextSideBySide leftText="CGST" rightText={`9%`} />
                  <AlingTextSideBySide leftText="SGST" rightText={`9%`} />
                </>
              ) : (
                <AlingTextSideBySide leftText="IGST" rightText={`18%`} />
              )}
              <AlingTextSideBySide leftText="Total" rightText={`₹${totalCharge}`} />
              <Grid container mt={2}>
                <Grid item xs={8} textAlign="left">
                  <Stack direction="row" spacing={0}>
                    <WsTypography bold>Prorated Amount:</WsTypography>
                    <Tooltip title="The amount which will be charged from today to the end of your billing cycle">
                      <InfoIcon />
                    </Tooltip>
                  </Stack>
                </Grid>
                <Grid item xs={4} textAlign="right">
                  <WsTypography bold>
                    {proratedAmount != '0' && proratedAmount != 'calculating'
                      ? `₹${proratedAmount}`
                      : proratedAmount}
                  </WsTypography>
                </Grid>
                <Divider sx={{ width: '100%', mt: 1 }} />
              </Grid>
              <Grid container mt={2}>
                <Grid item xs={8} textAlign="left">
                  <Stack direction="row" spacing={0}>
                    <WsTypography bold>Net Payable:</WsTypography>
                    <Tooltip title="The amount which will be charged from today to the end of your billing cycle">
                      <InfoIcon />
                    </Tooltip>
                  </Stack>
                </Grid>
                <Grid item xs={4} textAlign="right">
                  <WsTypography bold>
                    {proratedAmount != '0' && proratedAmount != 'calculating'
                      ? `₹${proratedAmount}`
                      : proratedAmount}
                  </WsTypography>
                </Grid>
                <Divider sx={{ width: '100%', mt: 1 }} />
              </Grid>
              <Stack mt={2}>
                <WsButton
                  size="large"
                  onClick={() => setWarnBeforePayment(true)}
                  disabled={
                    disableSubmit || proratedAmount === '0' || proratedAmount === 'calculating'
                  }
                >
                  Pay now
                </WsButton>
              </Stack>
            </Grid>
          </FormProvider>
        ) : (
          <>
            <Grid item xs={12} textAlign="center">
              <Typography
                color={WsTypoColors.PRIMARY}
                variant="h5"
                sx={{ fontSize: '25px', fontWeight: 'bold' }}
              >
                Purchase User
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <Item>
                <Typography variant="h6">
                  Please note that an amount of
                  <Typography
                    variant="h6"
                    sx={{ fontWeight: 'bold', display: 'inline', ml: 1, mr: 1 }}
                    color={WsTypoColors.BLACK}
                  >
                    {`₹ ${proratedAmount}`}
                  </Typography>
                  will be charged to your account when you make this payment.
                </Typography>
                <Stack direction="row" spacing={2} mt={3} justifyContent="center">
                  <WsButton onClick={handleClose} variant="outlined">
                    Cancel
                  </WsButton>
                  <Button
                    sx={{ width: '12%' }}
                    variant="contained"
                    disabled={disableSubmit}
                    onClick={onSubmit}
                  >
                    Pay
                  </Button>
                </Stack>
              </Item>
            </Grid>
          </>
        )}
      </Grid>
    </>
  );
};

export default RazorPayManagePlan;
